import { type FormEvent, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { forumBodyError, forumThreadDetailView } from '@lms/shared-types';
import { Loader } from '@lms/ui';
import { CourseHubShell } from '@/components/courses/CourseHubShell';
import { CourseForumAliasAside } from '@/features/course-forum/CourseForumAliasAside';
import { FORUM_PAGE_DESCRIPTION } from '@/features/course-forum/constants';
import { CourseForumThreadFlow } from '@/features/course-forum/CourseForumThreadFlow';
import { type ForumOverview, type ForumPost, type ForumThreadDetail } from '@/features/course-forum/types';
import { api } from '@/lib/api';

interface StudentCourseMeta {
  name: string;
  title?: string;
}

export function CourseForumThreadPage() {
  const { id, threadId } = useParams<{ id: string; threadId: string }>();
  const queryClient = useQueryClient();
  const [reply, setReply] = useState('');
  const [replyError, setReplyError] = useState<string | null>(null);

  const { data: course } = useQuery({
    queryKey: ['student', 'courses', id],
    queryFn: () => api<StudentCourseMeta>(`/student/courses/${id}`),
    enabled: Boolean(id),
  });

  const { data: forum } = useQuery({
    queryKey: ['student', 'courses', id, 'forum'],
    queryFn: () => api<ForumOverview>(`/student/courses/${id}/forum`),
    enabled: Boolean(id),
  });

  const { data: thread, isLoading, error } = useQuery({
    queryKey: ['student', 'courses', id, 'forum', 'threads', threadId],
    queryFn: () => api<ForumThreadDetail>(`/student/courses/${id}/forum/threads/${threadId}`),
    enabled: Boolean(id && threadId),
  });

  const replyMutation = useMutation({
    mutationFn: (body: string) =>
      api(`/student/courses/${id}/forum/threads/${threadId}/reply`, {
        method: 'POST',
        body: { body },
      }),
    onSuccess: async () => {
      setReply('');
      setReplyError(null);
      await queryClient.invalidateQueries({ queryKey: ['student', 'courses', id, 'forum'] });
      await queryClient.invalidateQueries({ queryKey: ['student', 'courses', id, 'forum', 'threads', threadId] });
    },
  });

  function handleReply(event: FormEvent) {
    event.preventDefault();
    const error = forumBodyError(reply);
    if (error) {
      setReplyError(error);
      return;
    }
    setReplyError(null);
    replyMutation.mutate(reply.trim());
  }

  function quotePost(post: ForumPost) {
    const quoted = post.body
      .trim()
      .split('\n')
      .slice(0, 4)
      .map((line) => `> ${line}`)
      .join('\n');
    setReply((current) => `${current.trim() ? `${current.trim()}\n\n` : ''}${quoted}\n\n`);
    setReplyError(null);
  }

  if (!id || !threadId) return null;

  const courseName = course?.name ?? course?.title ?? 'Curso';
  const view = forumThreadDetailView(isLoading, thread);
  const openingPost = view.status === 'ready' ? view.openingPost : undefined;
  const replies = view.status === 'ready' ? view.replies : [];

  return (
    <CourseHubShell
      courseId={id}
      courseName={courseName}
      activeTab="foro"
      description={FORUM_PAGE_DESCRIPTION}
      aside={forum?.myAnonAlias ? <CourseForumAliasAside alias={forum.myAnonAlias} /> : undefined}
      studentHub
    >
      <div className="lms-course-forum-page lms-course-forum-thread-page">
        {error ? (
          <p className="text-sm text-red-600" role="alert">
            {(error as Error).message}
          </p>
        ) : null}

        {view.status === 'ready' ? (
          <CourseForumThreadFlow
            thread={view.thread}
            openingPost={openingPost}
            replies={replies}
            backTo={`/mis-cursos/${id}/foro`}
            reply={reply}
            replyError={replyError ?? (replyMutation.error instanceof Error ? replyMutation.error.message : null)}
            replyPending={replyMutation.isPending}
            onReplyChange={(value) => {
              setReply(value);
              setReplyError(null);
            }}
            onReplySubmit={handleReply}
            onQuote={quotePost}
            replyTitle="Tu respuesta"
            replyPlaceholder="Escribe tu aporte o respuesta a este hilo..."
          />
        ) : error ? null : (
          <div className="flex justify-center py-12">
            <Loader />
          </div>
        )}
      </div>
    </CourseHubShell>
  );
}
